import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Search } from "lucide-react";
import { cn } from "../../utils/cn";

interface DropdownItem {
    key: string | number;
    label: React.ReactNode;
    icon?: React.ReactNode;
    onClick?: () => void;
    variant?: 'default' | 'r' | 'secondary';
    selected?: boolean;
    className?: string;
}

interface DropdownProps {
    trigger: React.ReactNode;
    items?: DropdownItem[];
    customContent?: React.ReactNode;
    className?: string;
    align?: 'left' | 'right';
    mode?: 'dropdown' | 'menu';
    matchTriggerWidth?: boolean;
    showIcons?: boolean;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
    closeOnSelect?: boolean;
    usePortal?: boolean; // New prop to optionally disable portal
    disableMinTriggerWidth?: boolean;
    searchProps?: {
        value: string;
        onChange: (value: string) => void;
        placeholder?: string;
    };
}

export const Dropdown: React.FC<DropdownProps> = ({
    trigger,
    items = [],
    customContent,
    className,
    align = 'left',
    mode = 'dropdown',
    matchTriggerWidth = false,
    showIcons = true,
    onOpenChange,
    open: controlledOpen,
    closeOnSelect = true,
    usePortal = true, // Default to true for better visibility
    disableMinTriggerWidth = false,
    searchProps,
}) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    // Strict positioning state
    const [positionStyle, setPositionStyle] = useState<React.CSSProperties>({});
    const [animateClass, setAnimateClass] = useState("opacity-0");

    // Handle outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!isOpen) return;

            // Check if click is inside trigger
            if (containerRef.current && containerRef.current.contains(event.target as Node)) {
                return;
            }

            // Check if click is inside dropdown
            if (dropdownRef.current && dropdownRef.current.contains(event.target as Node)) {
                return;
            }

            // Clicked outside both
            setInternalOpen(false);
            onOpenChange?.(false);
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            // Handle window resize/scroll to close or reposition
            // Handle window resize/scroll to close or reposition
            const handleResize = () => {
                setInternalOpen(false);
                onOpenChange?.(false);
            };

            const handleScroll = (e: Event) => {
                // If scroll is triggered by the dropdown content itself (or its children), ignore it
                if (dropdownRef.current && (e.target === dropdownRef.current || dropdownRef.current.contains(e.target as Node))) {
                    return;
                }
                setInternalOpen(false);
                onOpenChange?.(false);
            };

            window.addEventListener("resize", handleResize);
            window.addEventListener("scroll", handleScroll, true); // Capture scroll on any element

            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
                window.removeEventListener("resize", handleResize);
                window.removeEventListener("scroll", handleScroll, true);
            };
        }
    }, [isOpen, onOpenChange]);

    // Calculate position
    useLayoutEffect(() => {
        if (isOpen && containerRef.current) {
            const triggerRect = containerRef.current.getBoundingClientRect();
            const scrollX = window.scrollX;
            const scrollY = window.scrollY;

            // Initial simple positioning
            let top = triggerRect.bottom + scrollY + 4;
            let left = triggerRect.left + scrollX;
            let width = matchTriggerWidth ? triggerRect.width : undefined;

            // Alignment adjustment
            if (align === 'right') {
                left = triggerRect.right + scrollX - (dropdownRef.current?.offsetWidth || 0);
            }

            // Viewport boundary detection
            const viewportWidth = window.innerWidth;
            const dropdownWidth = dropdownRef.current?.offsetWidth || 0;

            // Adjust horizontal position if it overflows right side
            if (align === 'left' && !width && dropdownWidth > 0 && (left + dropdownWidth > viewportWidth)) {
                // Try aligning to right edge of trigger if that fits?? 
                // Or just shift it so it fits in viewport
                const maxLeft = viewportWidth - dropdownWidth - 10; // 10px padding
                left = Math.min(left, maxLeft);
                // Ensure it doesn't overflow left side (e.g. on mobile)
                left = Math.max(10, left);
            }

            const style: React.CSSProperties = {
                position: 'absolute',
                top: top,
                left: align === 'left' ? left : 'auto',
                right: align === 'right' ? (document.body.clientWidth - triggerRect.right - scrollX) : 'auto',
                maxWidth: Math.min(900, window.innerWidth - 24), // Don't overflow screen (Increased to 900 for calendars)
                zIndex: 100000,
                minWidth: (matchTriggerWidth || !disableMinTriggerWidth) ? triggerRect.width : 120, // Restore simple dynamic width
            };

            // Smart Flip Logic (Vertical)
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - triggerRect.bottom;
            const menuHeight = dropdownRef.current?.offsetHeight || 300; // Measure real height

            if (spaceBelow < menuHeight && triggerRect.top > menuHeight) {
                // Flip to top
                style.top = 'auto';
                style.bottom = (viewportHeight - triggerRect.top - scrollY) + 4;
            }

            setPositionStyle(style);

            // Animation start
            requestAnimationFrame(() => {
                setAnimateClass("opacity-100");
            });
        } else {
            setAnimateClass("opacity-0");
        }
    }, [isOpen, align, matchTriggerWidth]);

    const handleMouseEnter = () => {
        if (window.matchMedia('(hover: hover)').matches) {
            // Clear any existing close timeout
            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            // Add delay before opening
            timeoutRef.current = setTimeout(() => {
                setInternalOpen(true);
                onOpenChange?.(true);
            }, 150); // 150ms delay
        }
    };

    const handleMouseLeave = () => {
        if (window.matchMedia('(hover: hover)').matches) {
            // Clear any pending open timeout
            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            // Add delay before closing
            timeoutRef.current = setTimeout(() => {
                setInternalOpen(false);
                onOpenChange?.(false);
            }, 150);
        }
    };

    const handleClick = () => {
        // On mobile (no hover support), toggle on click
        // On desktop (hover support), don't toggle
        if (!window.matchMedia('(hover: hover)').matches) {
            const newState = !isOpen;
            setInternalOpen(newState);
            onOpenChange?.(newState);
        }
    };

    const content = (
        <div
            ref={dropdownRef}
            style={positionStyle}
            data-drawer-ignore="true"
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={cn(
                "fixed bg-w rounded-xl overflow-hidden transition-opacity duration-150 ease-out",
                "shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-b05",
                "w-max h-auto",
                animateClass
            )}
        >
            {customContent ? (
                customContent
            ) : (
                <div className="flex flex-col">
                    {searchProps && (
                        <div className="p-2 border-b border-b05 relative shrink-0">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-b4 pointer-events-none" />
                            <input
                                autoFocus
                                className="w-full pl-10 pr-4 py-2 bg-b02 rounded-lg text-sm border-0 focus:ring-0 placeholder:text-b4 text-b"
                                placeholder={searchProps.placeholder || "搜索"}
                                value={searchProps.value}
                                onChange={(e) => searchProps.onChange(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}
                    <div className="py-2 max-h-[220px] overflow-y-auto scrollbar-hide [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
                        {items.length > 0 ? items.map((item) => (
                            <div
                                key={item.key}
                                className={cn(
                                    "px-6 py-2.5 text-sm flex items-center gap-2 cursor-pointer transition-colors",
                                    !showIcons && "justify-center text-center",
                                    "text-b4 hover:bg-b02 hover:text-b",
                                    item.selected && "bg-b02 font-medium text-b",
                                    item.variant === 'r' && "text-r hover:text-r hover:bg-r/10",
                                    item.className
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    item.onClick?.();
                                    if (closeOnSelect) {
                                        setInternalOpen(false);
                                        onOpenChange?.(false);
                                    }
                                }}
                            >
                                {showIcons && item.icon && <span className="shrink-0">{item.icon}</span>}
                                {item.label}
                            </div>
                        )) : (
                            <div className="px-6 py-4 text-sm text-center text-b4">
                                无匹配项
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div
            className={cn("inline-block text-left", matchTriggerWidth ? "w-full" : "", className)}
            ref={containerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="cursor-pointer" onClick={handleClick}>
                {trigger}
            </div>

            {isOpen && (usePortal ? createPortal(content, document.body) : content)}
        </div>
    );
};
